import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowUpRight, ArrowDownRight, Filter, ShoppingBag, Trophy, Home, Search } from 'lucide-react';
import { formatApiError, useAuth } from '../../context/AuthContext';
import WalletButton from './WalletButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POSITIONS = [
  { key: 'ALL', label: 'Todos' },
  { key: 'GK',  label: 'GK' },
  { key: 'DEF', label: 'DEF' },
  { key: 'ATA', label: 'ATA' },
];

const SORTS = [
  { key: 'price_desc', label: 'Preço ↓' },
  { key: 'price_asc',  label: 'Preço ↑' },
  { key: 'avg_desc',   label: 'Média ↓' },
  { key: 'rating_desc',label: 'Rating ↓' },
];

const PriceDelta = ({ current, previous }) => {
  if (previous == null || previous === current) return null;
  const delta = current - previous;
  const up = delta > 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold px-1.5 py-0.5 rounded ${up ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
      <Icon className="w-3 h-3" /> {up ? '+' : ''}{delta.toFixed(1)}
    </span>
  );
};

const MarketPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [pos, setPos] = useState('ALL');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('avg_desc');
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(20);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/fantasy/market`);
        setPlayers(data || []);
      } catch (ex) { setErr(formatApiError(ex)); } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    let list = players.slice();
    if (pos !== 'ALL') list = list.filter((p) => p.preferred_position === pos);
    if (search.trim()) list = list.filter((p) => p.player_name.toLowerCase().includes(search.trim().toLowerCase()));
    list = list.filter((p) => (p.current_price >= minPrice) && (p.current_price <= maxPrice));
    const sorters = {
      price_desc: (a, b) => b.current_price - a.current_price,
      price_asc:  (a, b) => a.current_price - b.current_price,
      avg_desc:   (a, b) => (b.avg_points || 0) - (a.avg_points || 0),
      rating_desc:(a, b) => (b.rating || 0) - (a.rating || 0),
    };
    list.sort(sorters[sort] || sorters.avg_desc);
    return list;
  }, [players, pos, search, sort, minPrice, maxPrice]);

  const goEscalar = (p) => navigate(`/fantasy?add=${p.id}`);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900">
      <header className="border-b border-white/10 bg-slate-950/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30 shrink-0">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-white font-black leading-tight tracking-tight">Mercado de Transferências</div>
              <div className="text-[10px] sm:text-xs text-emerald-300/80 italic leading-tight">RatingPro Fantasy</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {user && <WalletButton balance={user.procoins_balance} />}
            <Link to="/fantasy" className="text-slate-300 hover:text-white text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5" data-testid="market-back-btn">
              <Home className="w-4 h-4" /> <span className="hidden sm:inline">Fantasy</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="bg-slate-900/60 backdrop-blur border border-white/10 rounded-2xl p-4 mb-5 space-y-3" data-testid="market-filters">
          <div className="flex items-center gap-3 flex-wrap">
            <Filter className="w-4 h-4 text-emerald-300" />
            {POSITIONS.map((p) => (
              <button key={p.key} onClick={() => setPos(p.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-black ${pos === p.key ? 'bg-emerald-500 text-slate-950' : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700/60'}`}
                data-testid={`market-pos-${p.key}`}>{p.label}</button>
            ))}
            <div className="flex-1 min-w-[180px] relative">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Procurar jogador..."
                className="w-full bg-slate-800/70 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="market-search" />
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {SORTS.map((s) => (
              <button key={s.key} onClick={() => setSort(s.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${sort === s.key ? 'bg-white text-slate-900' : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700/60'}`}
                data-testid={`market-sort-${s.key}`}>{s.label}</button>
            ))}
            <div className="flex items-center gap-2 text-xs text-slate-300">
              <span>Preço:</span>
              <input type="number" step="0.5" min="0" max="20" value={minPrice} onChange={(e) => setMinPrice(Number(e.target.value))}
                className="w-16 bg-slate-800/70 border border-white/10 rounded px-2 py-1 text-white" data-testid="market-min-price" />
              <span>—</span>
              <input type="number" step="0.5" min="0" max="20" value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value))}
                className="w-16 bg-slate-800/70 border border-white/10 rounded px-2 py-1 text-white" data-testid="market-max-price" />
              <span>€M</span>
            </div>
          </div>
        </div>

        {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-4">{err}</div>}

        {loading ? <div className="text-slate-300">A carregar...</div>
          : filtered.length === 0 ? <div className="text-slate-400 p-6 text-center bg-slate-900/40 rounded-2xl border border-white/5">Nenhum jogador corresponde aos filtros.</div>
          : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((p) => (
                <div key={p.id} className="bg-slate-900/60 border border-white/10 rounded-2xl p-4 flex flex-col gap-2" data-testid={`market-card-${p.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-800 border border-white/10 shrink-0">
                      {p.profile_image_url ? <img src={`${BACKEND_URL}${p.profile_image_url}`} alt="" className="w-full h-full object-cover" /> : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-black truncate text-sm">{p.player_name}</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] font-black uppercase tracking-widest bg-slate-800 text-emerald-300 border border-emerald-400/30 rounded px-1.5 py-0.5">{p.preferred_position}</span>
                        <span className="text-[11px] text-amber-300 font-bold">★ {p.rating}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-emerald-300 font-black tabular-nums">{p.current_price.toFixed(1)} €M</div>
                      <div className="mt-0.5"><PriceDelta current={p.current_price} previous={p.previous_price} /></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-1 text-[11px] text-center">
                    <div className="bg-slate-800/60 rounded-md py-1"><div className="text-slate-400">G</div><div className="text-white font-bold">{p.mini_stats?.goals ?? 0}</div></div>
                    <div className="bg-slate-800/60 rounded-md py-1"><div className="text-slate-400">A</div><div className="text-white font-bold">{p.mini_stats?.assists ?? 0}</div></div>
                    <div className="bg-slate-800/60 rounded-md py-1"><div className="text-slate-400">CS</div><div className="text-white font-bold">{p.mini_stats?.clean_sheets ?? 0}</div></div>
                    <div className="bg-amber-500/10 rounded-md py-1"><div className="text-amber-300">Média</div><div className="text-amber-200 font-black tabular-nums">{(p.avg_points || 0).toFixed(1)}</div></div>
                  </div>
                  <button onClick={() => goEscalar(p)}
                    className="mt-1 inline-flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black rounded-lg px-3 py-2 text-sm"
                    data-testid={`market-escalar-${p.id}`}>
                    <Trophy className="w-4 h-4" /> Escalar
                  </button>
                </div>
              ))}
            </div>
          )}
      </main>
    </div>
  );
};

export default MarketPage;
